
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuthSession } from '@/hooks/useAuthSession';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import CurrentAffairQuizResult from './CurrentAffairQuizResult';
import { Skeleton } from '@/components/ui/skeleton';

interface QuizResult {
  id: string;
  session_id: string;
  user_id: string;
  score: number;
  total_questions: number;
  percentage: number;
  difficulty: string;
  date_filter: string | null;
  time_period: string | null;
  completed_at: string;
  questions: any[];
  answers: (number | null)[];
}

const CurrentAffairsQuizResultDetail = () => {
  const { resultId } = useParams<{ resultId: string }>();
  const { userId } = useAuthSession();
  const [loading, setLoading] = useState<boolean>(true);
  const [result, setResult] = useState<QuizResult | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchResult = async () => {
      if (!resultId || !userId) return;
      
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('current_affairs_quiz_results')
          .select('*')
          .eq('id', resultId)
          .eq('user_id', userId)
          .single();
          
        if (error) {
          console.error('Error fetching quiz result:', error);
          return;
        }
        
        setResult(data as QuizResult);
      } catch (err) {
        console.error('Failed to fetch quiz result:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchResult();
  }, [resultId, userId]);

  if (loading) {
    return (
      <div className="container mx-auto p-4 max-w-7xl">
        <Button
          variant="ghost"
          onClick={() => navigate('/current-affairs/adaptive-quiz')}
          size="sm"
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        
        <div className="space-y-4">
          <Skeleton className="h-[600px] w-full" />
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="container mx-auto p-4 max-w-7xl">
        <Button
          variant="ghost"
          onClick={() => navigate('/current-affairs/adaptive-quiz')}
          size="sm"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        
        <div className="mt-8 text-center">
          <h2 className="text-2xl font-bold mb-2">Result Not Found</h2>
          <p className="text-muted-foreground mb-4">
            The quiz result you're looking for doesn't exist or you don't have permission to view it.
          </p>
          <Button onClick={() => navigate('/current-affairs')}>
            Go to Current Affairs
          </Button>
        </div>
      </div>
    );
  }

  // Calculate time spent (since we don't store it directly)
  const timeSpent = { minutes: 0, seconds: 0 };

  return (
    <div className="container mx-auto p-4 max-w-7xl">
      <Button
        variant="ghost"
        onClick={() => navigate('/current-affairs/adaptive-quiz')}
        size="sm"
        className="mb-4"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back
      </Button>
      
      <CurrentAffairQuizResult
        results={{
          score: result.score,
          totalAnswered: result.total_questions,
          percentage: result.percentage,
          difficultyBreakdown: calculateDifficultyBreakdown(result.questions, result.answers),
          dateInfo: { 
            date: result.date_filter || new Date(result.completed_at).toISOString().split('T')[0],
            timePeriod: result.time_period 
          },
          sessionId: result.session_id,
          adaptiveDifficulty: result.difficulty,
          averageAnswerTime: 0, // Not stored yet
          timeSpent,
          allQuestions: result.questions,
          allAnswers: result.answers
        }}
        timeSpent={timeSpent}
        questions={result.questions}
        answers={result.answers}
        difficulty={result.difficulty}
      />
    </div>
  );
};

// Helper function to calculate difficulty breakdown
function calculateDifficultyBreakdown(questions: any[], answers: (number | null)[]): Record<string, { total: number, correct: number }> {
  return questions.reduce((acc, q, idx) => {
    const answer = answers[idx];
    const difficulty = q.difficulty || 'medium';
    
    if (!acc[difficulty]) {
      acc[difficulty] = { total: 0, correct: 0 };
    }
    
    if (answer !== null) {
      acc[difficulty].total++;
      if (answer === q.correctAnswer) {
        acc[difficulty].correct++;
      }
    }
    
    return acc;
  }, {} as Record<string, { total: number, correct: number }>);
}

export default CurrentAffairsQuizResultDetail;
